import { useState } from "react";
import { DatePicker } from "@/components/DatePicker";
import { FieldError, ImportanceSelect, MoneyField, Req, SelectField } from "@/components/fields";
import { PriorityKind } from "@/generated/core/v1/family";
import {
  COMMON_PRIORITIES,
  type DraftPerson,
  type DraftPriority,
  FREQUENCY_LABEL,
  hasPriorityNamed,
  newPriority,
  PCT_OPTIONS,
} from "@/plan-logic/questionnaire/draft";

// v44: two sentences, the second ending on the interest
const DEBT_MSG =
  "Your debt isn’t something you rank against everything else. Abi works on it either way, and how hard depends on the interest.";

/**
 * "Retiring comfortably" (v39): the share of today's income wanted at retirement (50% to 200%) and the
 * age, sent as `RetirementSection{TARGET, income_replacement_percent, retirement_age}`. `pct`
 * undefined: a household whose retirement is still worked out from its spending (method SPENDING,
 * from before v39), kept until a share is picked.
 */
export type RetirementTarget = { pct?: number; age: number };

/** The share select's blank: keep working it out from spending (Update Data, Priorities → Edit). */
export const FROM_SPENDING = "From what you spend today";

/** "Pick 1 to 10" with "Ties are fine." under it (v40: no share-of-spare-money line). */
function Score({
  value,
  onChange,
  error,
  testId,
}: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  error?: string;
  testId?: string;
}) {
  return <ImportanceSelect value={value} onChange={onChange} error={error} testId={testId} />;
}

type Setter = (fn: (p: DraftPriority) => DraftPriority) => void;

/** "Roughly when?" (a goal's date, or when to buy the home). */
function WhenField({
  p,
  i,
  set,
  err,
}: {
  p: DraftPriority;
  i: number;
  set: Setter;
  err: (path: string) => string | undefined;
}) {
  return (
    <div className="field" style={{ margin: 0 }}>
      <label>
        Roughly when? <Req />
      </label>
      <DatePicker
        value={p.when}
        // a date the household picks for a child's education is theirs (no longer the year they turn 18)
        onChange={(d) => set((x) => ({ ...x, when: d, whenEdited: x.forChild ? true : undefined }))}
        mode="future"
        label="Roughly when"
      />
      <FieldError error={err(`priorities.${i}.when`)} />
    </div>
  );
}

/** The blank down payment's hint: the engine's default (no rule here, only its words). */
export const DOWN_PAYMENT_HINT = "Leave it blank and Abi plans with 20% of the price.";

/**
 * "Buying a home": the engine's home-buying goal. Its price (required), the down payment (blank =
 * 20% of the price), the date, and the score. The legal minimum is the engine's check; its message
 * comes back on the down payment.
 */
function HomeFields({
  p,
  i,
  set,
  err,
}: {
  p: DraftPriority;
  i: number;
  set: Setter;
  err: (path: string) => string | undefined;
}) {
  return (
    <>
      <div className="two">
        <MoneyField
          label="Home price"
          required
          value={p.housePrice}
          onChange={(v) => set((x) => ({ ...x, housePrice: v }))}
          placeholder="e.g. 650,000"
          error={err(`priorities.${i}.housePrice`)}
          testId={`priority-${i}-price`}
          style={{ margin: 0 }}
        />
        <MoneyField
          label="Down payment (optional)"
          value={p.cost}
          onChange={(v) => set((x) => ({ ...x, cost: v }))}
          placeholder="20% of the price"
          hint={DOWN_PAYMENT_HINT}
          error={err(`priorities.${i}.cost`)}
          testId={`priority-${i}-down`}
          style={{ margin: 0 }}
        />
      </div>
      <div className="two" style={{ marginTop: 10 }}>
        <WhenField p={p} i={i} set={set} err={err} />
        <Score
          value={p.importance}
          onChange={(v) => set((x) => ({ ...x, importance: v }))}
          error={err(`priorities.${i}.importance`)}
          testId={`priority-${i}-importance`}
        />
      </div>
      <p className="hint" style={{ marginTop: 8 }}>
        Abi&rsquo;s plan buys the home on that date if the whole down payment is there, with a
        mortgage for the rest.
      </p>
    </>
  );
}

/**
 * The common-priorities picker (`#pri-picker`). Each arrives filled in with how it usually repeats;
 * one already on the list is greyed out, and adding it again is refused (v39).
 */
export function PriorityPicker({
  priorities,
  onAdd,
}: {
  priorities: DraftPriority[];
  onAdd: (p: DraftPriority) => void;
}) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 9 }} data-testid="priority-picker">
      {COMMON_PRIORITIES.map((c) => {
        const on = hasPriorityNamed(priorities, c.name);
        return (
          <button
            type="button"
            key={c.name}
            className="mini"
            disabled={on}
            style={on ? { opacity: 0.4 } : undefined}
            onClick={() => {
              if (!hasPriorityNamed(priorities, c.name))
                onAdd(newPriority(c.kind, c.name, c.repeats, c.frequency));
            }}
          >
            {on ? "✓ " : "+ "}
            {c.name}
          </button>
        );
      })}
    </div>
  );
}

/**
 * One priority's row in the list (`renderMine`): its cost, when, 1-10 score and repeat. The education
 * Abi added for a child (`forChild`) is tagged, dated to the year they turn 18, and never repeats.
 */
export function PriorityRow({
  p,
  i,
  onChange,
  onRemove,
  kids,
  err,
}: {
  p: DraftPriority;
  /** Its index in the whole list (test ids, error paths). */
  i: number;
  onChange: (next: DraftPriority) => void;
  onRemove: () => void;
  kids: DraftPerson[];
  err: (path: string) => string | undefined;
}) {
  const set: Setter = (fn) => onChange(fn(p));
  const auto = !!p.forChild;
  const child = auto ? kids.find((k) => k.uuid === p.forChild) : undefined;
  return (
    <div className="rowbox" data-testid={`priority-${i}`}>
      <div className="rowhead">
        <b>{p.name || "Something of your own"}</b>
        <span className="lvlchip">
          {p.importance ? `${p.importance} out of 10` : "Not scored yet"}
        </span>
        {auto && (
          <span className="tag" style={{ marginLeft: 8 }}>
            Added by Abi
          </span>
        )}
        <button type="button" className="rm" onClick={onRemove}>
          Remove
        </button>
      </div>
      {p.kind === PriorityKind.BUY_HOME ? (
        <HomeFields p={p} i={i} set={set} err={err} />
      ) : (
        <div className="three">
          <MoneyField
            label="Roughly what will it cost?"
            value={p.cost}
            onChange={(v) => set((x) => ({ ...x, cost: v }))}
            placeholder="Not sure"
            error={err(`priorities.${i}.cost`)}
            testId={`priority-${i}-cost`}
            style={{ margin: 0 }}
          />
          <WhenField p={p} i={i} set={set} err={err} />
          <Score
            value={p.importance}
            onChange={(v) => set((x) => ({ ...x, importance: v }))}
            error={err(`priorities.${i}.importance`)}
            testId={`priority-${i}-importance`}
          />
        </div>
      )}
      {auto ? (
        <p className="hint" style={{ marginTop: 9 }}>
          {child?.dob?.year
            ? `Dated to the year ${child.givenName.trim() || "your child"} turns 18, from the birthday you gave. Change it if you are planning around something else.`
            : "Dated to the year they turn 18 once you give their birthday. Change it if you are planning around something else."}
        </p>
      ) : (
        <>
          {p.kind === PriorityKind.CHILD_EDUCATION && kids.length > 0 && (
            <div className="field" style={{ margin: "10px 0 0" }}>
              <label>Who is it for?</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {kids.map((k) => {
                  const on = p.beneficiaryUuids.includes(k.uuid);
                  return (
                    <button
                      type="button"
                      key={k.uuid}
                      className={`chip${on ? " on" : ""}`}
                      aria-pressed={on}
                      onClick={() =>
                        set((x) => ({
                          ...x,
                          beneficiaryUuids: on
                            ? x.beneficiaryUuids.filter((u) => u !== k.uuid)
                            : [...x.beneficiaryUuids, k.uuid],
                        }))
                      }
                    >
                      {k.givenName || "Your child"}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {p.kind !== PriorityKind.BUY_HOME && (
            <label className="repchk">
              <input
                type="checkbox"
                checked={p.repeats}
                onChange={(e) => set((x) => ({ ...x, repeats: e.target.checked }))}
              />{" "}
              This one repeats
            </label>
          )}
          {p.repeats && p.kind !== PriorityKind.BUY_HOME && (
            <div className="two" style={{ marginTop: 10 }}>
              <div className="field" style={{ margin: 0 }}>
                <label>How often</label>
                <select
                  value={p.frequency}
                  onChange={(e) => set((x) => ({ ...x, frequency: Number(e.target.value) }))}
                  aria-label="How often"
                >
                  {FREQUENCY_LABEL.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>
                  Until when <Req />
                </label>
                <DatePicker
                  value={p.until}
                  onChange={(d) => set((x) => ({ ...x, until: d }))}
                  mode="future"
                  label="Until when"
                />
                <FieldError error={err(`priorities.${i}.until`)} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The prototype's priorities list (`renderMine`): each priority's row (`PriorityRow`), plus the
 * always-on rows for paying off debt (shown, not scored) and retiring comfortably (scored, with the
 * share of today's income and the age, v39). Used by onboarding and by Priorities → Edit priorities.
 * Adding something already on the list is refused (v39).
 */
export function PriorityList({
  priorities,
  onChange,
  retirementImportance,
  onRetirementImportance,
  retirement,
  onRetirement,
  ageOptions,
  fromSpending = false,
  onRemoveEducation,
  hasDebt,
  kids,
  err,
}: {
  priorities: DraftPriority[];
  onChange: (next: DraftPriority[]) => void;
  retirementImportance: number | undefined;
  onRetirementImportance: (v: number | undefined) => void;
  /** The retirement share and age (omitted: the score alone). */
  retirement?: RetirementTarget;
  onRetirement?: (next: RetirementTarget) => void;
  /** The ages offered (`retirementAgeOptions`). */
  ageOptions?: number[];
  /** Offer the blank share (`FROM_SPENDING`): the stored household works it out from spending. */
  fromSpending?: boolean;
  /** A child's education Abi added was removed (onboarding doesn't add it again). */
  onRemoveEducation?: (childUuid: string) => void;
  hasDebt: boolean;
  kids: DraftPerson[];
  err: (path: string) => string | undefined;
}) {
  const [custom, setCustom] = useState<string | null>(null);
  const dup = custom !== null && !!custom.trim() && hasPriorityNamed(priorities, custom);
  const addCustom = () => {
    if (!custom?.trim() || dup) return;
    onChange([...priorities, newPriority(PriorityKind.CUSTOM, custom.trim())]);
    setCustom(null);
  };
  const ages = ageOptions ?? Array.from({ length: 26 }, (_, k) => 50 + k);

  return (
    <div data-testid="priority-list">
      {hasDebt && (
        <div className="rowbox">
          <div className="rowhead">
            <span className="lockic">&#9881;</span>
            <b>Paying off debt</b>
            <span className="tag" style={{ marginLeft: 8 }}>
              Always on
            </span>
          </div>
          <p className="lockmsg">{DEBT_MSG}</p>
        </div>
      )}
      <div className="rowbox" data-testid="priority-retirement">
        <div className="rowhead">
          <span className="lockic">&#9881;</span>
          <b>Retiring comfortably</b>
          <span className="lvlchip">
            {retirementImportance ? `${retirementImportance} out of 10` : "Not scored yet"}
          </span>
          <span className="tag" style={{ marginLeft: 8 }}>
            Always on
          </span>
        </div>
        <div className="three">
          {retirement && onRetirement && (
            <>
              <SelectField
                label="Share of today’s income you’d want at retirement"
                value={retirement.pct ?? 0}
                options={[
                  ...(fromSpending || retirement.pct === undefined
                    ? ([[0, FROM_SPENDING]] as [number, string][])
                    : []),
                  ...PCT_OPTIONS.map((v): [number, string] => [v, `${v}%`]),
                ]}
                onChange={(v) => onRetirement({ ...retirement, pct: v || undefined })}
                testId="ret-pct"
                style={{ margin: 0 }}
              />
              <SelectField
                label="Age you’d want to retire"
                value={retirement.age}
                options={ages.map((a): [number, string] => [a, String(a)])}
                onChange={(age) => onRetirement({ ...retirement, age })}
                error={err("retirement.age")}
                testId="ret-age"
                style={{ margin: 0 }}
              />
            </>
          )}
          <Score
            value={retirementImportance}
            onChange={onRetirementImportance}
            error={err("retirementImportance")}
            testId="retirement-importance"
          />
        </div>
      </div>
      {!priorities.length && (
        <div className="empty">Nothing else yet. Pick from the list above, or add your own.</div>
      )}
      {priorities.map((p, i) => (
        <PriorityRow
          key={p.uuid}
          p={p}
          i={i}
          kids={kids}
          err={err}
          onChange={(next) => onChange(priorities.map((x, k) => (k === i ? next : x)))}
          onRemove={() => {
            onChange(priorities.filter((_, k) => k !== i));
            if (p.forChild) onRemoveEducation?.(p.forChild);
          }}
        />
      ))}
      {custom === null ? (
        <button
          type="button"
          className="mini"
          style={{ marginTop: 12 }}
          onClick={() => setCustom("")}
          data-testid="priority-add-custom"
        >
          + Add something of your own
        </button>
      ) : (
        <div className="rowbox" style={{ marginTop: 12 }}>
          <div className="field" style={{ margin: 0 }}>
            <label htmlFor="pri-custom">What is it?</label>
            <input
              id="pri-custom"
              type="text"
              value={custom}
              autoFocus
              aria-invalid={dup || undefined}
              onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCustom();
                }
              }}
            />
            <FieldError error={dup ? `${custom.trim()} is already on your list.` : undefined} />
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
            <button
              type="button"
              className="mini"
              disabled={!custom.trim() || dup}
              onClick={addCustom}
            >
              Add it
            </button>
            <button type="button" className="mini" onClick={() => setCustom(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
